// src/components/about/PhilosophySection.jsx
import React from 'react';
import pic2 from '../../assets/img21.jpg';
import pic3 from '../../assets/img33.jpg';
import pic4 from '../../assets/img37.jpg';

const philosophies = [
  {
    number: "01",
    title: "Uncluttered. Personal.",
    desc: "Spaces designed with monastic simplicity yet wrapped in mindful luxury. No excess. Only what serves your healing. Every room breathes prana — infused with natural materials, soft light, and silence that speaks.",
    img: pic2,
    gradient: "from-emerald-500 to-teal-500",
    bgColor: "emerald-100"
  },
  {
    number: "02",
    title: "Refresh. Resonate.",
    desc: "Step into gardens alive with medicinal herbs, flowing water, and birdsong. Biophilic design merges architecture with nature — so every breath becomes therapy and every glance restores the soul.",
    img: pic3,
    gradient: "from-teal-500 to-cyan-500",
    bgColor: "teal-100",
    reverse: true
  },
  {
    number: "03",
    title: "Tailored. Tantalising.",
    desc: "Sattvic cuisine prepared fresh each day — aligned with your dosha, season, and healing phase. Food is not just nourishment; it is alchemy. Every bite recalibrates your inner ecosystem.",
    img: pic4,
    gradient: "from-emerald-500 to-amber-500",
    bgColor: "amber-100"
  }
];

export default function PhilosophySection() {
  return (
    <section className="py-32 px-6 md:px-12 lg:px-32 bg-gradient-to-b from-white via-emerald-50/30 to-white overflow-hidden">
  <div className="max-w-7xl mx-auto">
    {/* Section Title with Decorative Line */}
    <div className="text-center mb-20">
      <p className="text-sm tracking-widest uppercase text-emerald-700 font-medium mb-4">Our Ethos</p>
      <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 relative inline-block">
        Every Corner Heals
        <span className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-transparent via-emerald-600 to-transparent"></span>
      </h2>
    </div>

    {/* Philosophy Blocks with Staggered Animations & Depth */}
    <div className="space-y-32">
      {/* Block 1 */}
      <div className="grid lg:grid-cols-2 gap-16 items-center group">
        <div className="space-y-8 translate-y-10 opacity-0 animate-fadeInUp animation-delay-200">
          <div className="flex items-center gap-4">
            <span className="text-6xl font-bold text-emerald-600/20">01</span>
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900">Uncluttered. Personal.</h3>
          </div>
          <p className="text-xl text-gray-600 leading-relaxed max-w-lg">
            Spaces designed with monastic simplicity yet wrapped in mindful luxury. 
            No excess. Only what serves your healing. Every room breathes prana — 
            infused with natural materials, soft light, and silence that speaks.
          </p>
          <div className="h-1 w-24 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full"></div>
        </div>

        <div className="relative">
          <div className="absolute inset-0 translate-x-4 translate-y-4 bg-emerald-100 rounded-3xl -z-10 group-hover:translate-x-6 group-hover:translate-y-6 transition-all duration-700"></div>
          <img 
            src={pic2} 
            alt="Sanctuary Room" 
            className="rounded-3xl shadow-2xl object-cover h-[500px] w-full group-hover:scale-[1.02] transition-transform duration-700"
          />
        </div>
      </div>

      {/* Block 2 */}
      <div className="grid lg:grid-cols-2 gap-16 items-center group">
        <div className="relative order-last lg:order-first">
          <div className="absolute inset-0 -translate-x-4 translate-y-4 bg-teal-100 rounded-3xl -z-10 group-hover:-translate-x-6 group-hover:-translate-y-6 transition-all duration-700"></div>
          <img 
            src={pic3} 
            alt="Healing Gardens" 
            className="rounded-3xl shadow-2xl object-cover h-[500px] w-full group-hover:scale-[1.02] transition-transform duration-700"
          />
        </div>

        <div className="space-y-8 translate-y-10 opacity-0 animate-fadeInUp animation-delay-500">
          <div className="flex items-center gap-4">
            <span className="text-6xl font-bold text-emerald-600/20">02</span>
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900">Refresh. Resonate.</h3>
          </div>
          <p className="text-xl text-gray-600 leading-relaxed max-w-lg">
            Step into gardens alive with medicinal herbs, flowing water, and birdsong. 
            Biophilic design merges architecture with nature — so every breath becomes therapy 
            and every glance restores the soul.
          </p>
          <div className="h-1 w-24 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-full"></div>
        </div>
      </div>

      {/* Block 3 */}
      <div className="grid lg:grid-cols-2 gap-16 items-center group">
        <div className="space-y-8 translate-y-10 opacity-0 animate-fadeInUp animation-delay-800">
          <div className="flex items-center gap-4">
            <span className="text-6xl font-bold text-emerald-600/20">03</span>
            <h3 className="text-4xl md:text-5xl font-bold text-gray-900">Tailored. Tantalising.</h3>
          </div>
          <p className="text-xl text-gray-600 leading-relaxed max-w-lg">
            Sattvic cuisine prepared fresh each day — aligned with your dosha, season, and healing phase. 
            Food is not just nourishment; it is alchemy. Every bite recalibrates your inner ecosystem.
          </p>
          <div className="h-1 w-24 bg-gradient-to-r from-emerald-500 to-amber-500 rounded-full"></div>
        </div>

        <div className="relative">
          <div className="absolute inset-0 translate-x-4 translate-y-4 bg-amber-100 rounded-3xl -z-10 group-hover:translate-x-6 group-hover:translate-y-6 transition-all duration-700"></div>
          <img 
            src={pic4} 
            alt="Ayurvedic Cuisine" 
            className="rounded-3xl shadow-2xl object-cover h-[500px] w-full group-hover:scale-[1.02] transition-transform duration-700"
          />
        </div>
      </div>
    </div>
  </div>
</section>
  );
}